import { Button, Field, Input } from '@payaca/ambrosia';
import { getRegionalTextString } from '@payaca/helpers/internationalHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import { FC, useMemo, useState, type JSX } from 'react';

import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { RegionalStrings } from '@payaca/types/internationalTypes';
import { Address } from '@payaca/types/locationTypes';
import { useRegion } from '@/api/rest/users/useRegion';
import { AddressLookupSelect } from '../addressLookupSelect/AddressLookupSelect';

type AddressType = Pick<
  Address,
  'line1' | 'line2' | 'city' | 'administrativeArea' | 'postcode' | 'country'
>;

interface Props {
  address?: Partial<AddressType>;
  onChange: (address?: Partial<AddressType>) => void;
  touchedState?: { [fieldName: string]: boolean };
  validationState?: { [fieldName: string]: FieldValidationResult };
  onTouch?: (fieldName: string) => void;
  disabled?: boolean;
  disableManualEdit?: boolean;
  onClear?: () => void;
  /** Account region, used to label the postcode and administrative-area fields. Defaults to UK. */
  region?: AccountRegions;
}

export const AddressLookupOrInput: FC<Props> = ({
  address,
  onChange,
  onTouch,
  touchedState,
  validationState,
  disabled,
  disableManualEdit,
  onClear,
  region: regionProp,
}: Props): JSX.Element => {
  const accountRegion = useRegion();
  const region = regionProp ?? accountRegion;
  const [enterManually, setEnterManually] = useState(false);

  const hasATouchedField =
    touchedState !== undefined && Object.keys(touchedState).length > 0;
  const hasAnError = !!(
    validationState &&
    Object.values(validationState).find(
      (v) => v && v.errors && v.errors.length > 0
    )
  );
  return (
    <div className="flex flex-col items-start gap-3">
      {!enterManually ? (
        <GeoSearchAddress
          address={address}
          onChange={onChange}
          disabled={disabled}
          onEnterManually={() => setEnterManually(true)}
          isIncomplete={hasATouchedField && hasAnError}
          disableManualEdit={disableManualEdit}
          onClear={onClear}
        />
      ) : (
        <EnterAddressManually
          address={address}
          onChange={onChange}
          onTouch={onTouch}
          touchedState={touchedState}
          validationState={validationState}
          disabled={disabled}
          onLookupAddress={() => setEnterManually(false)}
          region={region}
        />
      )}
    </div>
  );
};

const GeoSearchAddress: FC<
  Pick<Props, 'address' | 'onChange' | 'disabled' | 'validationState'> & {
    onEnterManually: () => void;
    isIncomplete: boolean;
    disableManualEdit?: boolean;
    onClear?: () => void;
  }
> = (props) => {
  const {
    address,
    onChange,
    onEnterManually,
    disabled,
    isIncomplete,
    disableManualEdit,
    onClear,
  } = props;

  const isDirty = useMemo(() => {
    return (
      !!address?.line1 ||
      !!address?.line2 ||
      !!address?.city ||
      !!address?.administrativeArea ||
      !!address?.postcode
    );
  }, [address]);

  return (
    <>
      <AddressLookupSelect
        address={address}
        onChange={onChange}
        disabled={disabled}
      />
      {isIncomplete && (
        <div className="text-red-500">
          Address incomplete, please edit manually
        </div>
      )}
      {!disableManualEdit && (
        <div>
          <Button disabled={disabled} variant="link" onClick={onEnterManually}>
            {isDirty ? 'Edit' : 'Enter'} manually
          </Button>
        </div>
      )}
      {!!onClear && (
        <Button disabled={disabled} variant="link" onClick={onClear}>
          Choose existing address
        </Button>
      )}
    </>
  );
};

const EnterAddressManually: FC<Props & { onLookupAddress?: () => void }> = (
  props
) => {
  const {
    address,
    onChange,
    onTouch,
    touchedState,
    validationState,
    onLookupAddress,
    disabled,
    region,
  } = props;
  return (
    <>
      <AddressInput
        address={address}
        onChange={onChange}
        onTouch={onTouch}
        touchedState={touchedState}
        validationState={validationState}
        region={region}
      />
      {onLookupAddress && (
        <div>
          <Button disabled={disabled} variant="link" onClick={onLookupAddress}>
            Lookup address
          </Button>
        </div>
      )}
    </>
  );
};

const AddressInput: FC<{
  address?: Partial<AddressType>;
  onChange: (address: Partial<AddressType>) => void;
  touchedState?: { [fieldName: string]: boolean };
  validationState?: { [fieldName: string]: FieldValidationResult };
  onTouch?: (fieldName: string) => void;
  disabled?: boolean;
  region?: AccountRegions;
}> = ({
  address,
  onChange,
  touchedState,
  validationState,
  onTouch,
  disabled,
  region,
}) => {
  return (
    <fieldset className="flex w-full flex-col gap-2 border-0 p-0">
      <Field.Legacy
        name="line1"
        validationState={
          validationState?.['line1']?.isValid === false &&
          touchedState?.['line1']
            ? {
                isValid: false,
                validationMessages: validationState?.['line1']?.errors,
              }
            : undefined
        }
      >
        <Field.Label>Address line 1</Field.Label>
        <Input
          value={address?.line1 || ''}
          onChange={(value) => onChange({ ...address, line1: value })}
          onTouch={() => onTouch?.('line1')}
          disabled={disabled}
        />
      </Field.Legacy>
      <Field.Legacy
        name="line2"
        validationState={
          validationState?.['line2']?.isValid === false &&
          touchedState?.['line2']
            ? {
                isValid: false,
                validationMessages: validationState?.['line2']?.errors,
              }
            : undefined
        }
      >
        <Field.Label>Address line 2</Field.Label>
        <Input
          value={address?.line2 || ''}
          onChange={(value) => onChange({ ...address, line2: value })}
          onTouch={() => onTouch?.('line2')}
          disabled={disabled}
        />
      </Field.Legacy>
      <Field.Legacy
        name="city"
        validationState={
          validationState?.['city']?.isValid === false && touchedState?.['city']
            ? {
                isValid: false,
                validationMessages: validationState?.['city']?.errors,
              }
            : undefined
        }
      >
        <Field.Label>Town / city</Field.Label>
        <Input
          value={address?.city || ''}
          onChange={(value) => onChange({ ...address, city: value })}
          onTouch={() => onTouch?.('city')}
          disabled={disabled}
        />
      </Field.Legacy>
      <Field.Legacy
        name="administrativeArea"
        validationState={
          validationState?.['administrativeArea']?.isValid === false &&
          touchedState?.['administrativeArea']
            ? {
                isValid: false,
                validationMessages:
                  validationState?.['administrativeArea']?.errors,
              }
            : undefined
        }
      >
        <Field.Label>
          {getRegionalTextString(region, RegionalStrings.ADMINISTRATIVE_AREA)}
        </Field.Label>
        <Input
          value={address?.administrativeArea || ''}
          onChange={(value) =>
            onChange({ ...address, administrativeArea: value })
          }
          onTouch={() => onTouch?.('administrativeArea')}
          disabled={disabled}
        />
      </Field.Legacy>
      <Field.Legacy
        name="postcode"
        validationState={
          validationState?.['postcode']?.isValid === false &&
          touchedState?.['postcode']
            ? {
                isValid: false,
                validationMessages: validationState?.['postcode']?.errors,
              }
            : undefined
        }
      >
        <Field.Label>
          {getRegionalTextString(region, RegionalStrings.POSTCODE)}
        </Field.Label>
        <Input
          value={address?.postcode || ''}
          onChange={(value) => onChange({ ...address, postcode: value })}
          onTouch={() => onTouch?.('postcode')}
          disabled={disabled}
        />
      </Field.Legacy>
    </fieldset>
  );
};
